import React, { useState, useEffect } from "react";
import {
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBAlert,
  MDBRadio,
  MDBSpinner,
} from "mdb-react-ui-kit";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { saveAs } from "file-saver";
import filterStandingCommittees from "../../utils/filterStandingCommittees";
import addCustomFooter from "../../utils/addCustomFooter";
import ReactGA from "react-ga4";

function ExportModal({
  toggleOpen,
  basicModal,
  filteredCommittees,
  selectedCommitteeTypes,
  setBasicModal,
  selectedLegislators,
  selectedChambers,
  selectedBills,
  selectedBillTypes,
  dateRange,
}) {
  const [loading, setLoading] = useState(false);
  const [fileType, setFileType] = useState("pdf"); // Reset fileType to "pdf" whenever the modal is opened
  let onlyMeetingsWithBillHearing = false;
  const [sortBy, setSortBy] = useState("meetingTime");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    if (basicModal) {
      setFileType("pdf");
    }
  }, [basicModal]);

  const getDateSuffix = () => {
    const now = new Date();
    return `${(now.getMonth() + 1).toString().padStart(2, "0")}-${now
      .getDate()
      .toString()
      .padStart(2, "0")}-${now.getFullYear()}`;
  };

  // const getFormattedDate = () => {
  //   const now = new Date();
  //   const date = now.toLocaleDateString(); // Format the date
  //   const time = now.toLocaleTimeString([], {
  //     hour: "2-digit",
  //     minute: "2-digit",
  //   }); // Format the time without seconds
  //   return `${date} ${time}`;
  // };

  const sortTableData = (dataRows) => {
    const columnMap = {
      meetingTime: 1, // Meeting Date & Time column index
      billNumber: 3, // Bill # column index
      sponsor: 5, // Sponsor column index
      committee: 0, // Committee Name column index
    };

    const columnIndex = columnMap[sortBy];

    const sortedRows = dataRows.sort((a, b) => {
      const valA = a[columnIndex] || "";
      const valB = b[columnIndex] || "";

      if (sortBy === "meetingTime") {
        // Parse the meetingTime strings into Date objects
        const dateA = new Date(valA);
        const dateB = new Date(valB);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      } else {
        // Default: case-insensitive string comparison
        const normalizedA = valA.toString().toLowerCase();
        const normalizedB = valB.toString().toLowerCase();
        if (normalizedA < normalizedB) {
          return sortOrder === "asc" ? -1 : 1;
        }
        if (normalizedA > normalizedB) {
          return sortOrder === "asc" ? 1 : -1;
        }
        return 0;
      }
    });

    return sortedRows;
  };

  const generatePDF = async (data) => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    // Define margin values (36 points = 0.5 inch)
    const margin = 36;
    const pageWidth = doc.internal.pageSize.width;
    const logoPath = "/logo192.png";

    const convertToBase64 = async (path) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = path;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
      });
    };

    const logoBase64 = await convertToBase64(logoPath);
    const logoWidth = 40;
    const logoHeight = 40;

    doc.addImage(logoBase64, "PNG", margin, 20, logoWidth, logoHeight);

    doc.setFontSize(12);
    doc.text("69th Legislature 2025", pageWidth / 2, margin - 15, {
      align: "center",
    });
    doc.setFontSize(16);
    doc.text("Committees - Meeting Details", pageWidth / 2, margin + 5, {
      align: "center",
    });

    if (selectedLegislators && selectedLegislators.legs.length > 0) {
      doc.setFontSize(10);

      const formattedRoles =
        selectedLegislators.roles.length === 1
          ? selectedLegislators.roles[0]
          : selectedLegislators.roles.slice(0, -1).join(", ") +
            " and " +
            selectedLegislators.roles[selectedLegislators.roles.length - 1];

      const sponsorText = `For ${formattedRoles}: ${selectedLegislators.legs
        .map((legislator) => `${legislator.lastName}, ${legislator.firstName}`)
        .join(" & ")}`;

      doc.text(sponsorText, pageWidth / 2, margin + 20, { align: "center" });

      if (selectedCommitteeTypes && selectedCommitteeTypes.length > 0) {
        doc.setFontSize(8);
        doc.text(
            `Committee Type(s): ${selectedCommitteeTypes
                .join(", ")}`,
            pageWidth / 2,
            margin + 30,
            {
              align: "center",
            }
        );
      }
      if (selectedChambers && selectedChambers.length > 0) {
        doc.setFontSize(8);
        doc.text(
          `In Chamber(s): ${selectedChambers
            .map((chamber) => chamber.exportValue)
            .join(", ")}`,
          pageWidth / 2,
          margin + 40,
          {
            align: "center",
          }
        );
      }
      if (selectedBills && selectedBills.length > 0) {
        doc.setFontSize(8);
        doc.text(
          `Bill(s): ${selectedBills
            .map((bill) => bill.exportValue)
            .join(", ")}`,
          pageWidth / 2,
          margin + 50,
          {
            align: "center",
          }
        );
      }
    } else {
      if (selectedCommitteeTypes && selectedCommitteeTypes.length > 0) {
        doc.setFontSize(8);
        doc.text(
            `Committee Type(s): ${selectedCommitteeTypes
                .join(", ")}`,
            pageWidth / 2,
            margin + 30,
            {
              align: "center",
            }
        );
      }
      if (selectedChambers && selectedChambers.length > 0) {
        doc.setFontSize(8);
        doc.text(
          `In Chamber(s): ${selectedChambers
            .map((chamber) => chamber.exportValue)
            .join(", ")}`,
          pageWidth / 2,
          margin + 40,
          {
            align: "center",
          }
        );
      }
      if (selectedBills && selectedBills.length > 0) {
        doc.setFontSize(8);
        doc.text(
          `Bill(s): ${selectedBills
            .map((bill) => bill.exportValue)
            .join(", ")}`,
          pageWidth / 2,
          margin + 50,
          {
            align: "center",
          }
        );
      }
    }

    const tableData = data.flatMap((committee) => {
      const name = committee.committeeDetails.committeeCode.name || "";
      return committee.meetingTimes.flatMap((meeting) => {
        if (meeting.bills.length === 0) {
          return [
            [
              name,
              new Date(meeting.meetingTime).toLocaleString(),
              meeting.location || "",
              "",
              "",
              "",
            ],
          ];
        } else {
          return meeting.bills.map((bill) => [
            name,
            new Date(meeting.meetingTime).toLocaleString(),
            meeting.location || "No location",
            bill.billNumber || "No bill number",
            bill.shortTitle || "No short title",
            bill.sponsor || "No sponsor",
          ]);
        }
      });
    });

    // Map column names to indices
    const columnMap = {
      Committee: 0,
      "Meeting Date & Time": 1,
      Location: 2,
      Bill: 3,
      "Short Title": 4,
      Sponsor: 5,
    };
    const columnIndex = columnMap[sortBy];

    // Sort tableData using the correct sorting function
    const sortedTableData = sortTableData(
      tableData,
      columnIndex,
      sortOrder === "asc"
    );

    doc.autoTable({
      head: [
        [
          "Committee",
          "Meeting Date & Time",
          "Location",
          "Bill #",
          "Short Title",
          "Sponsor",
        ],
      ],
      body: sortedTableData, // Use the sorted data
      startY: margin + 55,
      margin: { left: margin, right: margin }, // Set margins so the table fits within the page
      headStyles: { fillColor: [103, 137, 127] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      styles: { fontSize: 8 },
    });

    // Add footer after all pages are generated
    addCustomFooter(doc, margin);

    if (typeof doc.putTotalPages === "function") {
      doc.putTotalPages("{total_pages_count_string}");
    }

    doc.save(`${getDateSuffix()}-Committee-Meeting-Details.pdf`);
  };

  const generateCSV = async (data) => {
    const rows = [
      [
        "Committee",
        "Meeting Date & Time",
        "Location",
        "Bill #",
        "Short Title",
        "Sponsor",
      ],
    ];

    data.forEach((committee) => {
      const name = committee.committeeDetails.committeeCode.name || "";
      committee.meetingTimes.forEach((meeting) => {
        meeting.bills.forEach((bill) => {
          rows.push([
            name,
            new Date(meeting.meetingTime).toLocaleString(),
            meeting.location || "No location",
            bill.billNumber || "No bill number",
            bill.shortTitle || "No short title",
            bill.sponsor || "No sponsor",
          ]);
        });
      });
    });

    // Map sortBy to column index
    const columnMap = {
      Committee: 0,
      "Meeting Date & Time": 1,
      Location: 2,
      Bill: 3,
      "Short Title": 4,
      Sponsor: 5,
    };
    const columnIndex = columnMap[sortBy];

    // Helper function to escape CSV fields
    const escapeCsvField = (field) => {
      if (field === null || field === undefined) return "";
      const str = String(field);
      // Wrap the field in quotes and escape any existing quotes
      return `"${str.replace(/"/g, '""')}"`;
    };

    // Separate the header from the data rows
    const [header, ...dataRows] = rows;

    // Sort the data rows only
    const sortedRows = sortTableData(
      dataRows,
      columnIndex,
      sortOrder === "asc"
    );

    // Prepend the header row after sorting
    const finalRows = [header, ...sortedRows];

    const csvContent = finalRows
      .map((row) => row.map(escapeCsvField).join(",")) // Escape each field before joining
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `${getDateSuffix()}-Committee-Meeting-Details.csv`);
  };

  const handleExport = async () => {
    setLoading(true);
    const updatedCommittees = await filterStandingCommittees(
      filteredCommittees,
      dateRange,
      selectedLegislators,
      selectedChambers,
      selectedBills,
      onlyMeetingsWithBillHearing
    );
    if (fileType === "pdf") {
      ReactGA.event({
        category: "User Interaction",
        action: "PDF download",
        label: "Download",
      });
      await generatePDF(updatedCommittees);
    } else if (fileType === "csv") {
      ReactGA.event({
        category: "User Interaction",
        action: "CSV download",
        label: "Download",
      });
      await generateCSV(updatedCommittees);
    }
    setLoading(false);
    setBasicModal(false); // Close modal after export
  };

  return (
    <>
      <MDBModal
        open={basicModal}
        onClose={() => setBasicModal(false)}
        tabIndex="-1"
      >
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Export Options</MDBModalTitle>
              <MDBBtn
                className="btn-close"
                color="none"
                onClick={() => setBasicModal(false)}
              ></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <form>
                {selectedLegislators?.legs?.length === 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold">Meetings</h6>
                    <MDBRow>
                      <MDBCol>
                        <MDBRadio
                          name="meetingsType"
                          id="onlyBillHearing"
                          label="Only Bill Hearings"
                          onChange={() => (onlyMeetingsWithBillHearing = true)}
                        />
                      </MDBCol>
                      <MDBCol>
                        <MDBRadio
                          name="meetingsType"
                          id="allBillHearings"
                          label="All Scheduled"
                          defaultChecked
                          onChange={() => (onlyMeetingsWithBillHearing = false)}
                        />
                      </MDBCol>
                    </MDBRow>
                  </div>
                )}
                <div className="mb-4">
                  <h6 className="fw-bold">Export File Type</h6>
                  <MDBRow>
                    <MDBCol>
                      <MDBRadio
                        name="fileType"
                        id="csv"
                        label="Excel/CSV"
                        onChange={() => setFileType("csv")}
                      />
                    </MDBCol>
                    <MDBCol>
                      <MDBRadio
                        name="fileType"
                        id="pdf"
                        label="Print/PDF"
                        defaultChecked
                        onChange={() => setFileType("pdf")}
                      />
                    </MDBCol>
                  </MDBRow>
                </div>
                <div className="mb-4">
                  <MDBRow>
                    <MDBCol>
                      <h6 className="fw-bold">Sort By</h6>
                      <select
                        className="form-select"
                        onChange={(e) => setSortBy(e.target.value)}
                        value={sortBy}
                        defaultValue="meetingTime"
                      >
                        <option value="meetingTime" defaultChecked>
                          Meeting Date & Time
                        </option>
                        <option value="billNumber">Bill #</option>
                        <option value="sponsor">Sponsor</option>
                        <option value="committee">Committee</option>
                      </select>
                    </MDBCol>
                  </MDBRow>
                  <MDBRow className="mt-1">
                    <MDBCol>
                      <MDBRadio
                        name="sortOrder"
                        id="ascending"
                        label="Ascending"
                        onChange={() => setSortOrder("asc")}
                        checked={sortOrder === "asc"}
                      />
                    </MDBCol>
                    <MDBCol>
                      <MDBRadio
                        name="sortOrder"
                        id="descending"
                        label="Descending"
                        onChange={() => setSortOrder("desc")}
                        checked={sortOrder === "desc"}
                      />
                    </MDBCol>
                  </MDBRow>
                </div>
              </form>
              <MDBAlert open className="w-100" color="warning">
                <strong>NOTE:</strong> The export will include results based on
                the filters currently applied. Adjust filters to refine the data
                before exporting.
              </MDBAlert>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn onClick={handleExport} disabled={loading}>
                {loading ? (
                  <>
                    Generating File{" "}
                    <MDBSpinner size="sm" role="status" className="ms-2" />
                  </>
                ) : (
                  "Download"
                )}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </>
  );
}

export default ExportModal;
